// src/store/useResourceStore.ts
import { create } from "zustand";
import LocalStorageService from "service/localStorageService";
import {
    getAllJSON,
    fetchMetricInfo,
    getTimezoneList,
} from "service/jsonService";

interface ResourceState {
    // State
    isResourcesLoaded: boolean;
    isResourcesLoading: boolean;
    error: Error | null;

    // Actions
    checkResourcesLoaded: () => boolean;
    loadResources: () => Promise<void>;
    resetResources: () => void;
}

export const useResourceStore = create<ResourceState>((set, get) => ({
    // Initial state
    isResourcesLoaded: false,
    isResourcesLoading: false,
    error: null,

    // Check if resources are loaded
    checkResourcesLoaded: () => {
        const requiredKeys = ["listOfJSON", "metricInfo", "timezones"];
        const missing = requiredKeys.find(
            (k) => !LocalStorageService.getItem(k)
        );
        const loaded = !missing;

        set({ isResourcesLoaded: loaded });
        return loaded;
    },

    // Load resources
    loadResources: async () => {
        const { isResourcesLoading, isResourcesLoaded } = get();

        // Prevent duplicate loading
        if (isResourcesLoading) {
            return;
        }

        // If already loaded, skip
        if (isResourcesLoaded && get().checkResourcesLoaded()) {
            return;
        }

        set({ isResourcesLoading: true, error: null });

        try {
            await Promise.all([
                getAllJSON(),
                fetchMetricInfo(),
                getTimezoneList(),
            ]);

            set({
                isResourcesLoaded: true,
                isResourcesLoading: false,
                error: null,
            });
        } catch (error) {
            console.error("Error loading resources:", error);
            set({
                isResourcesLoaded: false,
                isResourcesLoading: false,
                error: error as Error,
            });
            throw error;
        }
    },

    // Reset resources (e.g., on logout)
    resetResources: () => {
        set({
            isResourcesLoaded: false,
            isResourcesLoading: false,
            error: null,
        });
    },
}));
